import axios from "axios";
import type {AssertNoSpecDrift, RequestSpecDrift, Schemas} from "api/schemaAssert";
import {metaBrowserIdFor, type AdAttribution} from "./adAttribution";

/**
 * The authenticated, write-once acquisition endpoint (api#647).
 *
 * Auth rides the shared axios instance's request interceptor (`utils/AxiosUtils`), exactly as the referral
 * submit does — there is no second client and no second token path.
 */

/**
 * Exactly the fields the api accepts; `capturedAt` is browser bookkeeping and never leaves the device.
 * `fbp` is Meta's browser id, read from Meta's own cookie at submission and never stored by this app
 * (kitecorp/transferify#375, `adAttribution.metaBrowserIdFor`).
 */
export type AdAttributionPayload = Omit<AdAttribution, "capturedAt"> & {fbp?: string};

/**
 * OpenAPI conformance (#208), and the LAND-ORDER GATE for api#753.
 *
 * `AttributionChannel` is generated from the api's published enum, so `SpecDrift` fails — naming `channel`
 * — while this app's union carries a value the deployed contract does not. That makes "the api ships
 * first" a compile error rather than a deployment convention: a ui built against the old spec cannot send
 * `social` or `referral` to an api that would 400 them, and the same union is what the consent-free
 * `X-Signup-Channel` header carries, so the header vocabulary is gated by the same check.
 *
 * That last step is indirection: this binds to the attribution BODY's published enum, while the header
 * publishes its own copy (`components.parameters.SignupChannelHeader`, ui#1371). The two are one list in
 * the api and `signupChannelHeader.test.ts` asserts they stay one, so the gate keeps covering the header —
 * an assumption held by a test rather than by hope.
 */
export type _AdAttributionPayload = AssertNoSpecDrift<
	RequestSpecDrift<AdAttributionPayload, Schemas["AccountAttributionRequest"]>
>;

/**
 * Strips the local bookkeeping, adds Meta's browser id where the record may carry it, and drops absent
 * fields, so an empty value is never sent as `null`.
 *
 * @param metaBrowserId the `_fbp` cookie's value, or null when there is none
 */
export function attributionPayload(record: AdAttribution, metaBrowserId: string | null = null): AdAttributionPayload {
	const {capturedAt: _capturedAt, ...stored} = record;
	const payload: AdAttributionPayload = {...stored, fbp: metaBrowserIdFor(record, metaBrowserId)};
	return Object.fromEntries(
		Object.entries(payload).filter(([, value]) => value !== undefined),
	) as AdAttributionPayload;
}

export async function submitAdAttribution(payload: AdAttributionPayload): Promise<void> {
	await axios.post("/account/attribution", payload);
}
